import {
  getCatalogItem,
  placementScale,
  type ItemPlacement,
  type Plot
} from "@tribe/domain";
import { resolveVoxelAsset } from "./assets/catalog/itemAssets";
import type { VoxelData } from "./voxelTypes";

/**
 * Neighbours' gardens, flattened into the static world.
 *
 * Your own items each get their own group because you can pick them up. Nobody else's can be
 * touched, so they have no reason to stay addressable — and keeping them so would be ruinous: a
 * full guild is a hundred parcels, and a group per item would be thousands of draw calls for
 * scenery nobody interacts with. Poured into the shared instanced buffers instead, they cost
 * nothing beyond their voxels.
 *
 * The transform has to match `PlacedItemsLayer` exactly, or the same item would stand in one place
 * on its owner's screen and another on everybody else's.
 */

/** How low an asset's art reaches, in its own voxel units. */
function artBase(voxels: VoxelData[]): number {
  let lowest = 0;
  for (const voxel of voxels) lowest = Math.min(lowest, voxel.y - (voxel.scaleY ?? voxel.scale ?? 1) / 2);
  return lowest;
}

/**
 * Turns a point about Y using the generator's convention, which is the mirror of three.js's —
 * `placeAsset` and a group's `rotation.y = -angle` describe the same turn.
 */
function turn(x: number, z: number, cos: number, sin: number): { x: number; z: number } {
  return { x: x * cos - z * sin, z: x * sin + z * cos };
}

export function appendPlacementVoxels(
  out: VoxelData[],
  placements: ItemPlacement[],
  plot: Plot,
  surfaceY: number
): void {
  const plotCos = Math.cos(plot.rotationY);
  const plotSin = Math.sin(plot.rotationY);

  for (const placement of placements) {
    if (placement.surface !== "exterior") continue;

    let item;
    try {
      item = getCatalogItem(placement.itemId);
    } catch {
      continue; // An item that has left the catalogue is not worth a broken village.
    }
    const asset = resolveVoxelAsset(item, placement.selectedVariants);
    if (!asset) continue;

    const scale = placementScale(placement.scaleStep);
    const quarters = ((placement.rotationQuarters % 4) + 4) % 4;
    const itemAngle = quarters * (Math.PI / 2);
    const itemCos = Math.round(Math.cos(itemAngle));
    const itemSin = Math.round(Math.sin(itemAngle));
    const swapsAxes = quarters % 2 === 1;
    const baseY = artBase(asset.voxels) * scale;

    for (const voxel of asset.voxels) {
      // Scaled about the item's own origin, then stood on the ground the way the layer does.
      const local = turn(voxel.x * scale, voxel.z * scale, itemCos, itemSin);
      const onPlot = turn(
        local.x + placement.position.x,
        local.z + placement.position.z,
        plotCos,
        plotSin
      );

      const scaleX = (voxel.scaleX ?? voxel.scale ?? 1) * scale;
      const scaleZ = (voxel.scaleZ ?? voxel.scale ?? 1) * scale;

      const { scale: _uniform, ...rest } = voxel;
      out.push({
        ...rest,
        x: plot.position.x + onPlot.x,
        y: surfaceY + placement.position.y + voxel.y * scale - baseY,
        z: plot.position.z + onPlot.z,
        // A quarter turn swaps a box's footprint, which the shared buffer cannot express as a
        // rotation — it only stores position and scale — so it is baked into the scale here.
        scaleX: swapsAxes ? scaleZ : scaleX,
        scaleY: (voxel.scaleY ?? voxel.scale ?? 1) * scale,
        scaleZ: swapsAxes ? scaleX : scaleZ
      });
    }
  }
}
